export type OverlayLiveOnlyMutation = {
  liveDoc: string;
  /**
   * Operation names covered. Always explicit, even when every operation in the
   * document is live-only, so a mutation added to the document later is
   * unclassified until someone decides what it is.
   */
  operations: readonly string[];
  reason: string;
};

export type OverlayLiveOnlySurface = {
  /** Repo-relative path prefix. Files under it may write live docs on trunk. */
  prefix: string;
  reason: string;
};

/**
 * Mutation operations that write trunk on purpose, listed per operation. A
 * mutation added to one of these documents is unclassified until it is listed.
 */
export const OVERLAY_LIVE_ONLY_MUTATIONS: readonly OverlayLiveOnlyMutation[] = [
  {
    liveDoc: "src/comments/comments.graphql",
    operations: ["saveComment"],
    reason: "Comment threads are not org data.",
  },
  {
    liveDoc:
      "src/commonComponents/ProfileHeader/AvatarModal/mutations/deleteAvatarMutation.graphql",
    operations: ["deleteAvatarMutation"],
    reason: "Profile image storage, not org structure.",
  },
  {
    liveDoc:
      "src/commonComponents/ProfileHeader/AvatarModal/mutations/uploadAvatarMutation.graphql",
    operations: ["uploadAvatarMutation"],
    reason: "Profile image storage, not org structure.",
  },
  {
    liveDoc: "src/components/InviteUser/queries/person.graphql",
    operations: ["inviteUsers"],
    reason: "Workspace user invitations are access control.",
  },
  {
    liveDoc: "src/components/requisitionGuardrailApproval.graphql",
    operations: [
      "setWorkforceGuardrailRequestorReason",
      "undoWorkforceGuardrailApprovalStep",
      "respondWorkforceGuardrailApprovalStep",
    ],
    reason: "Requisition guardrail approval state, not org structure.",
  },
  {
    liveDoc: "src/components/EditRequisitionModal/updateRequisition.graphql",
    operations: ["updateRequisition", "updateRequisitionWithGuardrails"],
    reason:
      "Requisition (demand request) edits are Forecast data, not org structure.",
  },
  {
    liveDoc: "src/integrationTypes/allocation/queries.graphql",
    operations: ["createAllocationProject", "updateAllocationProject"],
    reason: "Allocation project configuration.",
  },
  {
    liveDoc: "src/pages/Datasource/datasource.gql",
    operations: [
      "createDatasource",
      "updateDatasource",
      "deleteDatasource",
      "exchangeLongTermKeys",
      "createImport",
      "deleteImport",
      "revertImport",
    ],
    reason: "Datasource and import management.",
  },
  {
    liveDoc: "src/queries/merge.graphql",
    operations: ["swapPublicTokenForAccountToken"],
    reason: "Integration token exchange for imports.",
  },
  {
    liveDoc: "src/pages/Designs/designs.graphql",
    operations: [
      "createDesign",
      "updateDesign",
      "deleteDesign",
      "reprovisionDesign",
      "redeprovisionDesign",
    ],
    reason: "Design lifecycle is the thing options belong to.",
  },
  {
    liveDoc: "src/pages/Designs/overlay/designOption.graphql",
    operations: [
      "createDesignOption",
      "updateDesignOption",
      "deleteDesignOption",
    ],
    reason: "Design option lifecycle is the thing options belong to.",
  },
  {
    liveDoc: "src/pages/OrgDesignSettings/supplyStructureTeams.graphql",
    operations: ["updateOrgDesignSupplyStructureRootSettings"],
    reason: "Org design settings are workspace configuration.",
  },
  {
    liveDoc:
      "src/pages/PersonDetailPage/sub-components/PersonWorkspaceAccess/personWorkspaceAccess.graphql",
    operations: ["setPersonWorkspaceAccess"],
    reason: "Workspace access is access control.",
  },
  {
    liveDoc: "src/queries/mutations/setSupportImpersonation.graphql",
    operations: ["setSupportImpersonation"],
    reason: "Support impersonation is access control.",
  },
  {
    liveDoc: "src/pages/SlidesPage/slideDecks.graphql",
    operations: [
      "createSlideDeck",
      "updateSlideDeck",
      "deleteSlideDeck",
      "createSlide",
      "updateSlide",
      "updateSlidePresentation",
      "deleteSlide",
      "reorderSlides",
    ],
    reason: "Slide decks are not org data.",
  },
  {
    liveDoc: "src/pages/WorkforceGuardrails/admin.graphql",
    operations: [
      "upsertWorkspaceWorkforceGuardrail",
      "upsertWorkspaceWorkforceGuardrailApproverGroup",
      "deleteWorkspaceWorkforceGuardrailApproverGroup",
      "setWorkspaceWorkforceGuardrailApprovalChain",
      "deleteWorkspaceWorkforceGuardrail",
    ],
    reason: "Workspace guardrail configuration.",
  },
  {
    liveDoc: "src/pages/WorkforceGuardrails/ruleImpactSimulation.graphql",
    operations: ["startSimulateWorkforceGuardrailRuleImpact"],
    reason:
      "Rule impact simulation is workspace guardrail analysis, not org structure.",
  },
  {
    liveDoc: "src/pages/Workspaces/workspace.graphql",
    operations: ["updateWorkspaceConfig"],
    reason: "Workspace configuration; the doc's tag operations are not listed.",
  },
  {
    liveDoc: "src/queries/ai.graphql",
    operations: [
      "updateAIDashboard",
      "upsertAIDashboard",
      "upsertDashboardLibraryItem",
      "reviewDashboardLibrarySuggestion",
      "deleteDashboardLibraryItem",
      "addDashboardLibraryItemToMine",
      "removeDashboardLibraryItemFromMine",
      "cloneDashboardLibraryItem",
    ],
    reason: "AI dashboards and the dashboard library.",
  },
  {
    liveDoc: "src/queries/mutations/aiChatMutations.graphql",
    operations: [
      "upsertAIChat",
      "upsertAIChatMessage",
      "updateAIChatMessageFeedback",
    ],
    reason: "AI chat history.",
  },
  {
    liveDoc: "src/queries/mutations/apiKeys.graphql",
    operations: [
      "createPublicAPICredential",
      "rotatePublicAPICredential",
      "deletePublicAPICredential",
    ],
    reason: "API credentials are not org data.",
  },
  {
    liveDoc: "src/queries/orgConfig.graphql",
    operations: ["updateOrgConfigForTenant", "updateOrgConfigForWorkspace"],
    reason: "Org and AI configuration.",
  },
  {
    liveDoc: "src/queries/orgImages.graphql",
    operations: ["createOrgImages", "deleteOrgImagesById"],
    reason: "Org image storage.",
  },
  {
    liveDoc: "src/queries/person.graphql",
    operations: ["addGroupToAccessGroups", "removeGroupFromAccessGroups"],
    reason: "Access group membership is access control.",
  },
  {
    liveDoc: "src/queries/userPreferences.graphql",
    operations: ["updateUserPreferencesForCurrentUser"],
    reason: "Per-user UI preferences.",
  },
  {
    liveDoc: "src/queries/workflows.graphql",
    operations: [
      "CreateWorkflow",
      "UpdateWorkflow",
      "PublishWorkflow",
      "DeleteWorkflow",
      "AddWorkflowStep",
      "UpdateWorkflowStep",
      "DeleteWorkflowStep",
      "TriggerWorkflow",
      "CancelWorkflowRun",
      "ApproveWorkflowStep",
      "RejectWorkflowStep",
      "CompleteFileUpload",
      "GetWorkflowStepOutputDownloadUrl",
      "RerequestWorkflowRun",
    ],
    reason: "Workflow automation is not org data.",
  },
];

export const OVERLAY_LIVE_ONLY_SURFACES: readonly OverlayLiveOnlySurface[] = [
  {
    prefix: "src/allocation/pages/",
    reason:
      "Forecast, Planner and Team Builder pages write trunk only. Their team tabs are blocked while an option is selected (OVERLAY_MUTATION_COVERAGE.md, Out of scope).",
  },
  {
    prefix: "src/allocation/components/HideTeamModal/",
    reason:
      "Only the Team Builder requestor tab mounts it, and that tab is blocked while an option is selected.",
  },
  {
    prefix: "src/pages/InternalDashboard/",
    reason:
      "Support and admin tooling acts on trunk and has no option context.",
  },
  {
    prefix: "src/pages/Workspaces/",
    reason: "Workspace configuration is not part of a design option.",
  },
];
